import {
  Component,
  Event,
  EventEmitter,
  h,
  Listen,
  Host,
  Prop,
  State,
  ComponentWillLoad,
  Element,
  VNode,
} from "@stencil/core"
import { DateFns, FP } from "../../deps"

import * as Events from "../../events/events"
import {
  Purchase,
  PurchaseState,
  PurchaseResaleOfferPricingDescriptor,
} from "../../service-sellalong/service-sellalong"
import * as Theme from "../../theme/theme"

export type State =
  | "OWNED"
  | "TOSELL"
  | "SELLING"
  | "SOLD"
  | "UNAVAILABLE"
  | "NEW"
  | "LOADING"

@Component({
  tag: "sellalong-panel-purchase-item",
  scoped: true,
  styleUrls: {
    theme_8629350: "./sellalong-panel-purchase-item-theme-sirplus.css",
    theme_27219324: "./sellalong-panel-purchase-item-theme-bmo.css",
    theme_53156151463: "./sellalong-panel-purchase-item.css",
  },
})
export class PanelPurchaseItem implements ComponentWillLoad {
  @Element()
  element!: HTMLElement

  private theme!: Theme.Theme

  /** A Purchase Item object from Sellalong */
  @Prop()
  purchase!: Purchase

  /**
   * Display variant for the panel item - passed down from the enclosing panel object
   */
  @Prop()
  variant: "mini" | "full" = "full"

  /** Fires when a purchase item is selected */
  @Event({ eventName: "sellalongPurchaseSelectIntent" })
  purchaseSelectIntent!: EventEmitter<Events.PurchaseStateTransitionIntent>

  @Listen("click")
  handleClick(event: Event) {
    event.preventDefault()
    const state = this.getState()

    if (!this.purchase || this.purchase.isSample) {
      return
    }

    if (state === "OWNED") {
      this.purchaseSelectIntent.emit({
        kind: "purchaseStateTransitionIntent",
        purchaseId: this.purchase.id,
        currentState: "OWNED",
        futureState: "TO_SELL",
      })
      this.state = PurchaseState.ToSell
    } else if (state === "TOSELL") {
      this.purchaseSelectIntent.emit({
        kind: "purchaseStateTransitionIntent",
        purchaseId: this.purchase.id,
        currentState: "TO_SELL",
        futureState: "OWNED",
      })
      this.state = PurchaseState.Owned
    }
  }

  @State()
  state: PurchaseState

  constructor() {
    this.state = this.purchase?.state || PurchaseState.Owned
  }

  async componentWillLoad() {
    this.theme = await Theme.loadTheme(this.element)()
  }

  getState(): State {
    if (!this.purchase) {
      return "LOADING"
    }

    switch (this.state) {
      case PurchaseState.Selling:
        return "SELLING"
      case PurchaseState.Sold:
        return "SOLD"

      case PurchaseState.Owned:
        if (!this.purchase.offer) {
          return "UNAVAILABLE"
        }
        if (this.purchase.offer.price === null) {
          if (
            this.purchase.offer.pricingDescriptor ===
            PurchaseResaleOfferPricingDescriptor.ExemptMinAge
          ) {
            return "NEW"
          }
          return "UNAVAILABLE"
        }
        return "OWNED"

      case PurchaseState.ToSell:
        return "TOSELL"
      case PurchaseState.Ordered:
        return "NEW"
    }
    return "UNAVAILABLE"
  }

  render() {
    const { settings, text } = this.theme
    const { purchase } = this
    const state = this.getState()

    if (purchase === null || state === "LOADING") {
      return
    }

    const isSelected = state === "TOSELL"

    const canTransition =
      !purchase.isSample && (state === "OWNED" || state === "TOSELL")

    const backgroundImageStyle = purchase.image
      ? { backgroundImage: `url(${purchase.image})` }
      : { backgroundColor: "#f3f3f3" }

    const offerPrice = FP.option.fromNullable(purchase.offer.price)

    const hostClass = {
      [`sellalong-panel-purchase-item-variant-${this.variant}`]: true,
      [`sellalong-panel-purchase-item-state-${state.toLowerCase()}`]: true,
      "sellalong-panel-purchase-item-sample": !!purchase.isSample,
    }

    const ctaAvailable =
      (state === "OWNED" || state === "TOSELL") && FP.option.isSome(offerPrice)
    const ctaText = purchase.isSample
      ? "Sample"
      : isSelected
      ? "Selected"
      : "Select"
    const ctaClasses =
      state === "OWNED" || state === "TOSELL"
        ? FP.function.pipe(
            settings.purchaseItemCtaClasses[state],
            FP.readonlyArray.reduce(
              {
                "sellalong-panel-purchase-item-select-button": true,
                "sellalong-panel-purchase-item-select-cta": !purchase.isSample,
              } as Record<string, boolean>,
              (acc, curr) => FP.readonlyRecord.upsertAt(curr, true)(acc)
            )
          )
        : undefined

    const statusMessage = text("panelItemStatusMessage")({
      state,
      minAge: settings.purchaseMinAge,
    })

    const footerFragment: VNode[] = [
      ctaAvailable ? <button class={ctaClasses}>{ctaText}</button> : null,

      statusMessage !== null ? (
        <div class="sellalong-panel-purchase-item-message">{statusMessage}</div>
      ) : null,
    ]

    const figureFragment: VNode = (
      <figure>
        <div
          class="sellalong-panel-purchase-item-image"
          style={backgroundImageStyle}
        ></div>
        <figcaption class="sellalong-panel-purchase-item-title">
          <div class="sellalong-panel-purchase-item-purchase-age">
            {text("panelItemPurchaseAge")(DateFns.parseISO(purchase.orderDate))}
          </div>

          {settings.purchaseItemShowSeller ? (
            <div class="sellalong-panel-purchase-item-seller">
              {purchase.seller}
            </div>
          ) : null}
          <div class="sellalong-panel-purchase-item-name" title={purchase.name}>
            {purchase.name}
          </div>

          {ctaAvailable ? (
            <div class="sellalong-panel-purchase-item-offer">
              <span>{text("panelItemFormatCurrency")(offerPrice)}</span>
            </div>
          ) : null}
        </figcaption>
      </figure>
    )

    return (
      <Host class={hostClass}>
        {canTransition ? (
          <a
            href="#sellalong"
            class="sellalong-panel-purchase-item-figure-wrapper"
          >
            {figureFragment}
          </a>
        ) : (
          <div class="sellalong-panel-purchase-item-figure-wrapper">
            {figureFragment}
          </div>
        )}

        {footerFragment}
      </Host>
    )
  }
}
